import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { FormField } from '../../../../service/types/types';
import { FormsService } from '../../../../service/forms.service';
import { DistributionsService } from '../../../../service/distributionList.service';
import { FeedbackModal } from '../../../common/feedback.modal';
import FormFieldsEditorMail from './formFieldsEditorMail';

// El GET /forms/:id devuelve las relaciones "distributionLists" (objetos), no el
// "distributionList" (ids) que se usa al guardar — por eso el cast al leer la respuesta.
interface DistributionListRef {
  id: string;
}

export default function FormEditMail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [formName, setFormName] = useState('');
  const [isSignRequired, setIsSignRequired] = useState(false);
  const [fields, setFields] = useState<FormField[]>([]);
  const [emailGroups, setEmailGroups] = useState<string[]>([]);
  const [distributionLists, setDistributionLists] = useState<{ value: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalMessage, setModalMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      const dl = await DistributionsService.findAll('arauco');
      setDistributionLists(dl.map((d) => ({ value: d.id, label: d.name })));

      if (!id) return;
      const form: any = await FormsService.findById(id);
      setFormName(form.name);
      setIsSignRequired(form.isSignRequired);
      setEmailGroups((form.distributionLists ?? []).map((d: DistributionListRef) => d.id));
      const sortedFields = [...(form.fields ?? [])]
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        .map((f: any) => ({
          ...f,
          distributionList: (f.distributionLists ?? []).map((d: DistributionListRef) => d.id),
        }));
      setFields(sortedFields);
    };
    load().finally(() => setLoading(false));
  }, [id]);

  const updateFormMails = (options: any) => {
    setEmailGroups(options ? options.map((option: any) => option.value) : []);
  };

  const handleSave = async () => {
    if (!id) return;
    if (!formName.trim()) {
      alert('Ingrese un nombre para el formulario');
      return;
    }
    if (fields.length === 0) {
      alert('Ingrese al menos un campo');
      return;
    }
    setSaving(true);
    try {
      await FormsService.update(id, {
        name: formName,
        isSignRequired,
        fields,
        distributionList: emailGroups,
      });
      setModalType('success');
      setModalMessage('Formulario actualizado correctamente');
      setShowModal(true);
      setTimeout(() => navigate('/arauco/form/list'), 2000);
    } catch {
      setModalType('error');
      setModalMessage('Error al actualizar el formulario');
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" size="sm" /> Cargando formulario...
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <FeedbackModal
        show={showModal}
        onHide={() => setShowModal(false)}
        type={modalType}
        message={modalMessage}
        autoClose
        duration={3000}
      />
      <h3>Editar Lista de Control</h3>
      <Form>
        <Form.Group className="mb-3 w-100">
          <Form.Label>Nombre Lista de Control</Form.Label>
          <Form.Control
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Ej: Encuesta de satisfacción"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="isSignRequired">
          <Form.Check
            type="checkbox"
            label="¿Requiere firma?"
            checked={isSignRequired}
            onChange={() => setIsSignRequired(!isSignRequired)}
          />
        </Form.Group>

        <FormFieldsEditorMail fields={fields} onChange={setFields} distributionLists={distributionLists} />

        <Row className="mt-2">
          <Col md={12}>
            <Select
              options={distributionLists}
              value={distributionLists.filter((opt) => emailGroups.includes(opt.value))}
              onChange={updateFormMails}
              className="w-100"
              isMulti
              placeholder="lista de distribución..."
              isSearchable
            />
          </Col>
        </Row>

        <br />
        <div className="d-flex gap-2">
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/arauco/form/list')} disabled={saving}>
            Cancelar
          </Button>
        </div>
      </Form>
      <br />
    </Container>
  );
}
